import * as projection from "@arcgis/core/geometry/projection";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import Geometry from "@arcgis/core/geometry/Geometry";
import Graphic from "@arcgis/core/Graphic";
import { PhaseErrorType } from "../components/features/master-create-study/main-form-create-study/main-form-create-study";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import GeoJSONLayer from "@arcgis/core/layers/GeoJSONLayer";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer";
import MapView from "@arcgis/core/views/MapView";
import { Layer } from "../interfaces/layer";
import {
  InfrastructureTypeIds,
  LayersDataTypes,
} from "../types/layers-data-types";
import MapLayer from "@arcgis/core/layers/Layer";
import Point from "@arcgis/core/geometry/Point";
import Polygon from "@arcgis/core/geometry/Polygon";
import Polyline from "@arcgis/core/geometry/Polyline";
import {
  CreatedObjectInfrastructure,
  CreatedObjectInfrastructureResults,
  InfrastructureTypeKey,
} from "../services/reducers/master-create-study";

type LinearUnits = "meters" | "kilometers";

export const verifyPhaseTerritory = async (
  outerGeometry: Geometry,
  innerGeometry: Graphic,
): Promise<{ isContains: boolean; projectedInnerGeometry: Graphic }> => {
  let transformedGeometry = innerGeometry.geometry;

  if (
    outerGeometry.spatialReference.wkid !==
    innerGeometry.geometry.spatialReference.wkid
  ) {
    await projection.load();

    const projected = projection.project(
      innerGeometry.geometry,
      outerGeometry.spatialReference,
    );

    if (!projected || Array.isArray(projected)) {
      return {
        isContains: false,
        projectedInnerGeometry: innerGeometry.clone(),
      };
    }

    transformedGeometry = projected;
  }

  const isContains = geometryEngine.contains(
    outerGeometry,
    transformedGeometry,
  );

  const projectedInnerGeometry = innerGeometry.clone();
  projectedInnerGeometry.geometry = transformedGeometry;

  return {
    isContains,
    projectedInnerGeometry,
  };
};

export const getPhaseTooltipText = (errorType: PhaseErrorType): string => {
  switch (errorType) {
    case "notPolygon":
      return "Please select a polygon on the map and try again.";
    case "sameAsSiteBoundary":
      return "The boundary of the site cannot be the boundary of the phase 1. Please select another object on the map and try again.";
    default:
      return "";
  }
};

export const supportsQueryFeatures = (
  layer: unknown,
): layer is FeatureLayer | GeoJSONLayer => {
  return layer instanceof FeatureLayer || layer instanceof GeoJSONLayer;
};

export async function checkLayerHasFeatures(
  viewer: MapView,
  layer: Layer,
): Promise<boolean> {
  try {
    const mapLayer = viewer.map.layers.find((l) => l.id === layer.name);

    if (!mapLayer) {
      return false;
    }

    await mapLayer.load();

    if (mapLayer.type === "feature") {
      const featureLayer = mapLayer as FeatureLayer;

      try {
        const count = await featureLayer.queryFeatureCount({ where: "1=1" });
        return count > 0;
      } catch {
        return false;
      }
    }

    if (mapLayer.type === "map-image") {
      const mapImageLayer = mapLayer as MapImageLayer;

      const sublayers = mapImageLayer.allSublayers.filter(
        (s) => typeof s.createFeatureLayer === "function",
      );

      if (sublayers.length === 0) {
        return false;
      }

      const results = await Promise.allSettled(
        sublayers.map(async (sublayer) => {
          try {
            const featureLayer = await sublayer.createFeatureLayer();
            if (!featureLayer) return false;

            const count = await featureLayer.queryFeatureCount({
              where: "1=1",
            });

            return count > 0;
          } catch {
            return false;
          }
        }),
      );

      return results.some(
        (result) => result.status === "fulfilled" && result.value,
      );
    }

    return false;
  } catch {
    return false;
  }
}

export async function getProjectedDistance(
  geomFrom: Geometry,
  geomTo: Geometry,
  unit: LinearUnits,
) {
  if (!geomFrom || !geomTo) return null;

  if (geomFrom.spatialReference?.wkid !== geomTo.spatialReference?.wkid) {
    const projected = projection.project(geomFrom, geomTo.spatialReference);
    if (!Array.isArray(projected)) {
      geomFrom = projected;
    } else {
      return null;
    }
  }

  return geometryEngine.distance(geomTo, geomFrom, unit);
}

export async function findMuscatAirportGeometry(airportLayers: MapLayer[]) {
  for (const layer of airportLayers) {
    if (!supportsQueryFeatures(layer)) continue;
    const result = await layer.queryFeatures({
      where: `NameEn = 'Muscat International Airport'`,
      outFields: ["*"],
      returnGeometry: true,
    });
    if (result.features.length > 0) {
      return result.features[0].geometry;
    }
  }
  return null;
}

export async function findNearestSeaports(
  seaportLayers: MapLayer[],
  targetGeom: Geometry,
) {
  const nearestPorts = [];

  for (const layer of seaportLayers) {
    if (!supportsQueryFeatures(layer)) continue;

    const result = await layer.queryFeatures({
      where: "1=1",
      outFields: ["*"],
      returnGeometry: true,
    });

    for (const feature of result.features) {
      const distance = await getProjectedDistance(
        feature.geometry,
        targetGeom,
        "kilometers",
      );
      if (distance !== null) {
        nearestPorts.push({
          name: feature.attributes?.NameEn,
          distance: distance,
        });
      }
    }
  }

  return nearestPorts
    .sort((a, b) => a.distance - b.distance)
    .slice(0, 2)
    .map((p) => ({
      name: p.name,
      distance: Math.round(p.distance),
    }));
}

export async function getInfrastructureAvailability(
  layersConfig: Layer[],
  dataType: LayersDataTypes,
  coordGeometry: Geometry,
  bufferGeometry: Geometry,
  viewer: MapView,
) {
  const matchingLayers = layersConfig
    .filter((l) => l.dataType === dataType)
    .map((l) => l.name);

  for (const name of matchingLayers) {
    const layer = viewer.map.layers.find((l) => l.id === name);
    if (!supportsQueryFeatures(layer)) continue;

    const result = await layer.queryFeatures({
      geometry: bufferGeometry,
      spatialRelationship: "intersects",
      outFields: ["*"],
      returnGeometry: true,
    });

    if (result.features.length > 0) {
      let minDistance = Infinity;

      for (const feature of result.features) {
        const dist = await getProjectedDistance(
          feature.geometry,
          coordGeometry,
          "meters",
        );
        if (dist !== null && dist < minDistance) {
          minDistance = dist;
        }
      }

      return {
        available: true,
        distance: minDistance !== Infinity ? Math.round(minDistance) : 0,
      };
    }
  }

  return { available: false, distance: 0 };
}

export async function getLogisticsDistances(
  layersConfig: Layer[],
  viewer: MapView,
  coordGeometry: Geometry,
) {
  const allLayers = viewer.map.layers.toArray();

  const airportLayers = allLayers.filter((l) =>
    layersConfig.some(
      (layerConfig) =>
        layerConfig.name === l.id &&
        layerConfig.dataType === LayersDataTypes.LogisticInfrastructure &&
        layerConfig.name === "OmanAirports",
    ),
  );

  const seaportLayers = allLayers.filter((l) =>
    layersConfig.some(
      (layerConfig) =>
        layerConfig.name === l.id &&
        layerConfig.dataType === LayersDataTypes.LogisticInfrastructure &&
        layerConfig.name === "OmanSeaports",
    ),
  );

  const muscatAirportGeometry = await findMuscatAirportGeometry(airportLayers);

  const airportDistance = muscatAirportGeometry
    ? Math.round(
        (await getProjectedDistance(
          muscatAirportGeometry,
          coordGeometry,
          "kilometers",
        )) ?? 0,
      )
    : null;

  const [nearestPort, secondNearestPort] = await findNearestSeaports(
    seaportLayers,
    coordGeometry,
  );

  return {
    airportDistance,
    FirstPortName: nearestPort?.name || null,
    FirstPortDist: nearestPort?.distance || null,
    SecondPortName: secondNearestPort?.name || null,
    SecondPortDist: secondNearestPort?.distance || null,
  };
}

export const getDistanceAndDirectionToInfrastructure = async (
  viewer: MapView,
  coordObjectGeometry: Geometry,
  layersConfig: Layer[],
): Promise<CreatedObjectInfrastructureResults> => {
  const results: {
    [key in InfrastructureTypeKey]?: CreatedObjectInfrastructure;
  } = {};
  const infrastructureTypesToCheck = InfrastructureTypeIds.filter(
    (typeId) => typeId !== LayersDataTypes.LogisticInfrastructure,
  ) as InfrastructureTypeKey[];
  const sourceWkid = coordObjectGeometry.spatialReference?.wkid;

  for (const infrastructureType of infrastructureTypesToCheck) {
    const matchingLayers = layersConfig
      .filter((l) => l.dataType === infrastructureType)
      .map((l) => l.name);
    let minDistance = null;
    let nearestTargetGeometry = null;

    for (const name of matchingLayers) {
      const layer = viewer.map.layers.find((l) => l.id === name);
      if (!supportsQueryFeatures(layer)) continue;

      const queryResult = await layer.queryFeatures({
        where: "1=1",
        outFields: ["*"],
        returnGeometry: true,
      });

      for (const feature of queryResult.features) {
        if (!feature.geometry) continue;

        const featureWkid = feature.geometry.spatialReference?.wkid;
        let targetGeometry = feature.geometry;

        if (featureWkid !== sourceWkid) {
          try {
            const projected = projection.project(
              feature.geometry,
              coordObjectGeometry.spatialReference,
            );
            if (Array.isArray(projected)) {
              continue;
            }
            targetGeometry = projected;
          } catch (projectionError) {
            continue;
          }
        }

        try {
          const distance = geometryEngine.distance(
            coordObjectGeometry,
            targetGeometry,
            "kilometers",
          );

          if (
            distance !== null &&
            (minDistance === null || distance < minDistance)
          ) {
            minDistance = distance;
            nearestTargetGeometry = targetGeometry;
          }
        } catch (distanceError) {
          continue;
        }
      }
    }

    let direction = null;
    if (nearestTargetGeometry && minDistance !== null && minDistance > 0) {
      direction = getVectorDirection(
        coordObjectGeometry,
        nearestTargetGeometry,
      );
    }

    results[infrastructureType] = {
      distance: minDistance ? Number(minDistance.toFixed(2)) : null,
      direction,
    };
  }

  return results;
};

function getCenterPoint(
  geometry: Geometry,
  targetGeometry: Geometry,
): Point | null {
  if (geometry instanceof Point) {
    return geometry;
  } else if (geometry instanceof Polygon) {
    return geometry.centroid;
  } else if (geometry instanceof Polyline) {
    const targetPoint = (targetGeometry as Polygon).centroid;
    const nearestResult = geometryEngine.nearestCoordinate(
      geometry,
      targetPoint,
    );
    return nearestResult.coordinate;
  } else {
    const extent = geometry.extent;
    if (extent) {
      return {
        x: (extent.xmin + extent.xmax) / 2,
        y: (extent.ymin + extent.ymax) / 2,
        spatialReference: geometry.spatialReference,
      } as Point;
    }
  }

  return null;
}

function getVectorDirection(
  fromGeometry: Geometry,
  toGeometry: Geometry,
): string | null {
  const fromPoint = getCenterPoint(fromGeometry, toGeometry);
  const toPoint = getCenterPoint(toGeometry, fromGeometry);
  if (!fromPoint || !toPoint) return null;

  const dx = toPoint.x - fromPoint.x;
  const dy = toPoint.y - fromPoint.y;

  let angleRadians = Math.atan2(dx, dy);
  let angleDegrees = angleRadians * (180 / Math.PI);

  if (angleDegrees < 0) {
    angleDegrees += 360;
  }

  const directions = [
    "north",
    "northeast",
    "east",
    "southeast",
    "south",
    "southwest",
    "west",
    "northwest",
  ];

  const index = Math.round((angleDegrees + 22.5) / 45) % 8;

  return directions[index];
}
